import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { EditorTab } from '@/types/routes';
import { ALL_EDITOR_TABS } from '@/types/routes';

interface RouterState {
  tab: EditorTab;
  navigate: (tab: EditorTab) => void;
}

const RouterContext = createContext<RouterState | null>(null);

export function parseTabFromHash(hash: string): EditorTab {
  const qIdx = hash.indexOf('?');
  const path = qIdx >= 0 ? hash.slice(0, qIdx) : hash;
  const raw = path.replace(/^#\/?/, '');
  const [tabStr] = raw.split('/');
  return ALL_EDITOR_TABS.includes(tabStr as EditorTab) || tabStr === 'login'
    ? (tabStr as EditorTab)
    : 'races';
}

function parseHash(): { tab: EditorTab } {
  return { tab: parseTabFromHash(window.location.hash || '') };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(parseHash);

  const navigate = useCallback((tab: EditorTab) => {
    window.location.hash = `/${tab}`;
  }, []);

  useEffect(() => {
    const onHash = () => setState(parseHash());
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, []);

  return (
    <RouterContext.Provider value={{ tab: state.tab, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter(): RouterState {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be inside RouterProvider');
  return ctx;
}
